import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImageOff } from "lucide-react";
import { downloadFileLogActivity } from "../../api/ProjectApi";

interface ImageViewProps {
  fileName: string;
  className?: string;
  imgClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
  onNaturalSize?: (width: number, height: number) => void;
}

const ImageView = ({ fileName, className, imgClassName, onLoad, onError, onNaturalSize }: ImageViewProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isImageReady, setIsImageReady] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["image", fileName],
    queryFn: () => downloadFileLogActivity(fileName),
    enabled: !!fileName,
    staleTime: 60 * 1000,
  });

  // Reset ready state when fileName changes
  useEffect(() => {
    setIsImageReady(false);
  }, [fileName]);

  // Check if img is already cached (complete before onLoad fires)
  useEffect(() => {
    if (imgRef.current?.complete && data) {
      setIsImageReady(true);
      onLoad?.();
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSkeleton = isLoading || (!!data && !isImageReady);

  return (
    <div className={`relative shrink-0 overflow-hidden ${className}`}>
      {/* Skeleton overlay — fades out when image is ready */}
      <div
        className={`absolute inset-0 skeleton-shimmer rounded transition-opacity duration-500 ${
          showSkeleton ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Error state */}
      {!isLoading && !data && (
        <div className="flex items-center justify-center w-full h-full">
          <ImageOff className="text-gray-400 w-4 h-4" />
        </div>
      )}

      {/* Image — fades in when ready */}
      {data && (
        <img
          ref={imgRef}
          src={data}
          alt={fileName}
          className={`w-full h-full transition-opacity duration-300 ${imgClassName ?? "object-cover"} ${
            isImageReady ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => {
            setIsImageReady(true);
            onLoad?.();
            if (onNaturalSize && imgRef.current) {
              onNaturalSize(imgRef.current.naturalWidth, imgRef.current.naturalHeight);
            }
          }}
          onError={onError}
        />
      )}
    </div>
  );
};

export default ImageView;
